/**
 * Changelog API client (#1355).
 *
 * The entries live in `public.changelog_entries` (migration 069), synced from
 * `migrations/postgres/changelog/*.json` on every deploy; the BFF serves them
 * newest first, paged by `before` (exclusive `publishedAt`) and `beforeKey`
 * (the key of that last entry, which breaks ties between equal dates — an
 * entry switched on by an env flag can share its date with others, #1456).
 */

import { apiRequest } from '@/lib/api-client'
import { logWarn } from '@/lib/logger'

export type ChangelogEntryType = 'feature' | 'improvement' | 'fix'

export interface LocalizedText {
  de?: string
  en?: string
}

export interface ChangelogEntry {
  key: string
  type: ChangelogEntryType
  title: LocalizedText
  body: LocalizedText
  howTo?: LocalizedText
  issue?: string
  publishedAt: string
}

export interface ChangelogPage {
  entries: ChangelogEntry[]
  hasMore: boolean
}

const ENTRY_TYPES: readonly string[] = ['feature', 'improvement', 'fix']

function isEntry(value: unknown): value is ChangelogEntry {
  if (!value || typeof value !== 'object') return false
  const entry = value as Record<string, unknown>
  return (
    typeof entry.key === 'string' &&
    typeof entry.type === 'string' &&
    ENTRY_TYPES.includes(entry.type) &&
    typeof entry.title === 'object' && entry.title !== null &&
    typeof entry.body === 'object' && entry.body !== null &&
    typeof entry.publishedAt === 'string' &&
    !Number.isNaN(Date.parse(entry.publishedAt))
  )
}

export const changelogService = {
  /**
   * One page of entries. Throws when the request fails or the route is
   * missing (404) — the caller treats both as "no changelog right now".
   */
  async list(
    params: { limit?: number; before?: string; beforeKey?: string } = {},
  ): Promise<ChangelogPage> {
    const query = new URLSearchParams()
    if (params.limit) query.set('limit', String(params.limit))
    if (params.before) {
      query.set('before', params.before)
      if (params.beforeKey) query.set('beforeKey', params.beforeKey)
    }
    const suffix = query.toString()
    const data = await apiRequest<{ entries?: unknown; hasMore?: unknown }>(
      `/changelog${suffix ? `?${suffix}` : ''}`,
    )
    if (!data || !Array.isArray(data.entries)) {
      throw new Error('Changelog nicht verfügbar')
    }
    const entries = data.entries.filter(isEntry)
    if (entries.length !== data.entries.length) {
      logWarn(
        { context: 'ChangelogService', dropped: data.entries.length - entries.length },
        'Changelog entries with an unexpected shape were skipped',
      )
    }
    return { entries, hasMore: data.hasMore === true }
  },
}
